import { useMemo, useState } from "react";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import type { MenuCategory } from "@/types";

interface CategoryManagerDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function CategoryManagerDrawer({
  open,
  onClose,
}: CategoryManagerDrawerProps) {
  const { show } = useToast();
  const {
    categories,
    products,
    saveCategory,
    deleteCategory,
    toggleCategory,
    moveCategory,
  } = useStore();

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MenuCategory | null>(null);
  const [error, setError] = useState("");

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    products.forEach((product) => {
      result[product.categoryId] = (result[product.categoryId] ?? 0) + 1;
    });
    return result;
  }, [products]);

  if (!open) return null;

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) {
      setError("الرجاء إدخال اسم التصنيف");
      return;
    }
    if (categories.some((item) => item.name === name)) {
      setError("يوجد تصنيف بنفس الاسم");
      return;
    }
    setError("");
    saveCategory({
      id: `cat${Date.now()}`,
      name,
      icon: "ri-price-tag-3-line",
      isActive: true,
    });
    setNewName("");
    show("تمت إضافة التصنيف");
  };

  const startEdit = (category: MenuCategory) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const commitEdit = () => {
    if (!editingId) return;
    const name = editingName.trim();
    if (!name) {
      show("الرجاء إدخال اسم التصنيف", "error");
      return;
    }
    const target = categories.find((item) => item.id === editingId);
    if (target) {
      saveCategory({ ...target, name });
      show("تم تحديث اسم التصنيف");
    }
    setEditingId(null);
  };

  const requestDelete = (category: MenuCategory) => {
    const count = counts[category.id] ?? 0;
    if (count > 0) {
      show(
        `لا يمكن حذف «${category.name}» — انقل ${count} منتج إلى تصنيف آخر أولًا`,
        "error",
      );
      return;
    }
    setDeleteTarget(category);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteCategory(deleteTarget.id);
    show("تم حذف التصنيف", "warning");
    setDeleteTarget(null);
  };

  return (
    <div className="fixed inset-0 z-[70]">
      <div
        className="absolute inset-0 bg-foreground-950/50 yc-fade-in"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <div className="absolute inset-y-0 end-0 w-full max-w-lg bg-white yc-drawer-left flex flex-col shadow-pop">
        <div className="shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="text-lg font-bold text-foreground-950">
              إدارة التصنيفات
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              أضف التصنيفات ورتّبها وفعّلها، ولا يمكن حذف تصنيف يحتوي على منتجات.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="إغلاق"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div className="rounded-box border border-background-200 p-4">
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              تصنيف جديد
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={(event) => {
                  setNewName(event.target.value);
                  setError("");
                }}
                placeholder="مثال: العروض الخاصة"
                className="flex-1 h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-lg"></i>
                إضافة
              </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-foreground-950">
                التصنيفات الحالية
              </h4>
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                {categories.length} تصنيف
              </span>
            </div>

            {categories.length === 0 ? (
              <div className="rounded-box border border-dashed border-background-300 px-4 py-12 text-center">
                <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
                  <i className="ri-price-tag-3-line text-xl"></i>
                </div>
                <p className="text-sm text-foreground-500">
                  لا توجد تصنيفات بعد — أضف أول تصنيف من الأعلى.
                </p>
              </div>
            ) : (
              <ul className="space-y-2.5">
                {categories.map((category, index) => {
                  const count = counts[category.id] ?? 0;
                  const isEditing = editingId === category.id;
                  return (
                    <li
                      key={category.id}
                      className="rounded-btn border border-background-200 px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                          <i className={category.icon}></i>
                        </span>
                        <div className="min-w-0 flex-1">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editingName}
                              autoFocus
                              onChange={(event) =>
                                setEditingName(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Enter") commitEdit();
                              }}
                              className="w-full h-10 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
                            />
                          ) : (
                            <p className="text-sm font-semibold text-foreground-950">
                              {category.name}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-foreground-500">
                            {count} منتج
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={category.isActive}
                          aria-label={`${
                            category.isActive ? "تعطيل" : "تفعيل"
                          } ${category.name}`}
                          title={category.isActive ? "تعطيل التصنيف" : "تفعيل التصنيف"}
                          onClick={() =>
                            toggleCategory(category.id, !category.isActive)
                          }
                          className={`relative w-11 h-6 shrink-0 rounded-full transition-colors duration-200 cursor-pointer ${
                            category.isActive ? "bg-primary-500" : "bg-background-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                              category.isActive ? "start-5" : "start-0.5"
                            }`}
                          ></span>
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-100 pt-3">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={commitEdit}
                              className="inline-flex items-center gap-1.5 h-9 rounded-btn bg-primary-500 px-3 text-xs font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-check-line"></i>
                              حفظ
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="inline-flex items-center gap-1.5 h-9 rounded-btn bg-background-100 px-3 text-xs font-semibold text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                            >
                              إلغاء
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(category)}
                            className="inline-flex items-center gap-1.5 h-9 rounded-btn bg-background-100 px-3 text-xs font-semibold text-foreground-600 hover:bg-primary-100 hover:text-primary-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-edit-line"></i>
                            تعديل الاسم
                          </button>
                        )}
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveCategory(category.id, "up")}
                            disabled={index === 0}
                            title="تحريك للأعلى"
                            aria-label="تحريك للأعلى"
                            className="w-9 h-9 flex items-center justify-center rounded-btn bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-up-line"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => moveCategory(category.id, "down")}
                            disabled={index === categories.length - 1}
                            title="تحريك للأسفل"
                            aria-label="تحريك للأسفل"
                            className="w-9 h-9 flex items-center justify-center rounded-btn bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <i className="ri-arrow-down-line"></i>
                          </button>
                        </span>
                        <button
                          type="button"
                          onClick={() => requestDelete(category)}
                          title={count > 0 ? "التصنيف غير فارغ" : "حذف التصنيف"}
                          aria-label="حذف التصنيف"
                          className={`ms-auto inline-flex items-center gap-1.5 h-9 rounded-btn px-3 text-xs font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                            count > 0
                              ? "bg-background-100 text-foreground-400"
                              : "bg-red-50 text-red-600 hover:bg-red-100"
                          }`}
                        >
                          <i className="ri-delete-bin-6-line"></i>
                          حذف
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="حذف التصنيف"
        message={`هل أنت متأكد من حذف «${deleteTarget?.name ?? ""}»؟ التصنيف فارغ ولا يمكن التراجع عن الحذف.`}
        confirmLabel="حذف التصنيف"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}